import * as THREE from "three";
import { humanSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";

/**
 * Ambient tennis rally: two humans on opposite sides of a net, ball arcing between them.
 * Nets span north-south; players stand east and west, shuffling on the baseline.
 * Only the hitter swings at contact.
 */
export class ParkTennisMatch {
  readonly group = new THREE.Group();
  private readonly playerA: THREE.Mesh;
  private readonly playerB: THREE.Mesh;
  private readonly ball: THREE.Mesh;
  private readonly shadowA: THREE.Mesh;
  private readonly shadowB: THREE.Mesh;
  private readonly netX: number;
  private readonly netZ: number;
  private readonly half: number;
  private readonly groundY: (x: number, z: number) => number;
  private readonly readyA: ReturnType<typeof humanSprite>;
  private readonly swingA: ReturnType<typeof humanSprite>;
  private readonly readyB: ReturnType<typeof humanSprite>;
  private readonly swingB: ReturnType<typeof humanSprite>;
  private phase: number;
  private readonly period: number;
  private readonly seed: number;
  private ax: number;
  private az: number;
  private bx: number;
  private bz: number;
  private ay: number;
  private by: number;
  /** Locked contact points for the current half-rally. */
  private rallyFromX = 0;
  private rallyFromZ = 0;
  private rallyToX = 0;
  private rallyToZ = 0;
  private rallyFromY = 0;
  private rallyToY = 0;
  private lastHalf = -1;

  constructor(
    netX: number,
    netZ: number,
    groundY: (x: number, z: number) => number,
    variant = 0,
    phaseOffset = 0,
  ) {
    this.half = 2.75;
    this.netX = netX;
    this.netZ = netZ;
    this.groundY = groundY;
    this.ax = netX - this.half;
    this.az = netZ;
    this.bx = netX + this.half;
    this.bz = netZ;
    this.ay = groundY(this.ax, this.az);
    this.by = groundY(this.bx, this.bz);
    this.phase = phaseOffset;
    this.period = 2.55 + (variant % 3) * 0.2;
    this.seed = variant * 1.7 + phaseOffset;

    this.readyA = humanSprite("tennisReady", variant);
    this.swingA = humanSprite("tennisSwing", variant);
    this.readyB = humanSprite("tennisReady", variant + 11);
    this.swingB = humanSprite("tennisSwing", variant + 11);

    this.shadowA = makeShadow(this.readyA.width);
    this.shadowA.position.set(this.ax, this.ay + 0.02, this.az);
    this.shadowB = makeShadow(this.readyB.width);
    this.shadowB.position.set(this.bx, this.by + 0.02, this.bz);

    this.playerA = new THREE.Mesh(
      new THREE.PlaneGeometry(this.readyA.width, this.readyA.height),
      billboardMaterial(this.readyA.texture),
    );
    this.playerA.position.set(this.ax, this.ay + this.readyA.height * SPRITE_FOOT_Y, this.az);
    this.playerA.renderOrder = 3;

    this.playerB = new THREE.Mesh(
      new THREE.PlaneGeometry(this.readyB.width, this.readyB.height),
      billboardMaterial(this.readyB.texture),
    );
    this.playerB.position.set(this.bx, this.by + this.readyB.height * SPRITE_FOOT_Y, this.bz);
    this.playerB.renderOrder = 3;

    this.ball = new THREE.Mesh(
      new THREE.SphereGeometry(0.12, 10, 8),
      new THREE.MeshLambertMaterial({ color: 0xc6ff00 }),
    );
    this.ball.castShadow = false;

    this.group.add(this.shadowA, this.shadowB, this.playerA, this.playerB, this.ball);
    this.beginHalf(this.phase < 0.5);
  }

  update(dtSec: number, timeMs: number, camera: THREE.Camera): void {
    this.phase = (this.phase + dtSec / this.period) % 1;
    const t = this.phase;
    const forward = t < 0.5;
    const half = forward ? 0 : 1;
    if (half !== this.lastHalf) {
      this.beginHalf(forward);
    }

    const u = forward ? t * 2 : (t - 0.5) * 2;
    const ease = u * u * (3 - 2 * u);

    this.shufflePlayers(dtSec, timeMs, t, forward);

    const x = this.rallyFromX + (this.rallyToX - this.rallyFromX) * ease;
    const z = this.rallyFromZ + (this.rallyToZ - this.rallyFromZ) * ease;
    const arc = Math.sin(Math.PI * ease) * 1.35;
    this.ball.position.set(x, Math.max(this.rallyFromY, this.rallyToY) + 0.55 + arc, z);

    // Only the hitter swings at contact - never both.
    const hitWindow = 0.07;
    const aSwing = t < hitWindow || t > 1 - hitWindow;
    const bSwing = Math.abs(t - 0.5) < hitWindow;
    setHumanMap(this.playerA, aSwing ? this.swingA : this.readyA);
    setHumanMap(this.playerB, bSwing ? this.swingB : this.readyB);

    const bobA = 1 + Math.sin(timeMs * 0.007 + this.seed) * 0.014;
    const bobB = 1 + Math.sin(timeMs * 0.0075 + this.seed * 1.3) * 0.014;
    this.playerA.position.set(
      this.ax,
      this.ay + this.readyA.height * SPRITE_FOOT_Y * bobA + (aSwing ? 0.06 : 0),
      this.az,
    );
    this.playerB.position.set(
      this.bx,
      this.by + this.readyB.height * SPRITE_FOOT_Y * bobB + (bSwing ? 0.06 : 0),
      this.bz,
    );
    this.shadowA.position.set(this.ax, this.ay + 0.02, this.az);
    this.shadowB.position.set(this.bx, this.by + 0.02, this.bz);

    faceCameraYaw(this.playerA, camera);
    faceCameraYaw(this.playerB, camera);
    this.playerA.scale.set(facingFlipSign(this.ax, this.az, 0, camera), 1, 1);
    this.playerB.scale.set(facingFlipSign(this.bx, this.bz, Math.PI, camera), 1, 1);
  }

  destroy(): void {
    this.group.removeFromParent();
    this.playerA.geometry.dispose();
    this.playerB.geometry.dispose();
    this.ball.geometry.dispose();
    (this.ball.material as THREE.Material).dispose();
  }

  private beginHalf(forward: boolean): void {
    this.lastHalf = forward ? 0 : 1;
    const rally = Math.floor(this.phase + this.seed * 10 + (forward ? 0 : 50));
    const fromZ = this.baselineAim(rally);
    const toZ = this.baselineAim(rally + 17);
    if (forward) {
      this.rallyFromX = this.homeX(false);
      this.rallyFromZ = fromZ;
      this.rallyToX = this.homeX(true);
      this.rallyToZ = toZ;
    } else {
      this.rallyFromX = this.homeX(true);
      this.rallyFromZ = fromZ;
      this.rallyToX = this.homeX(false);
      this.rallyToZ = toZ;
    }
    this.rallyFromY = this.groundY(this.rallyFromX, this.rallyFromZ);
    this.rallyToY = this.groundY(this.rallyToX, this.rallyToZ);
  }

  private shufflePlayers(dtSec: number, timeMs: number, t: number, forward: boolean): void {
    const lateral = 1.55;
    const depth = 0.55;
    const follow = Math.min(1, dtSec * 4.5);

    if (forward) {
      // A steps in to hit; B slides to receive.
      const aHit = smoothPulse(t, 0, 0.2);
      const bRecv = smoothPulse(t, 0.25, 0.5);
      const aTargetZ = lerp(this.idleZ(0), this.rallyFromZ, Math.max(aHit, 0.35));
      const bTargetZ = lerp(this.idleZ(1), this.rallyToZ, Math.max(bRecv, 0.45));
      this.az = lerp(this.az, aTargetZ, follow);
      this.bz = lerp(this.bz, bTargetZ, follow);
      this.ax = lerp(this.ax, this.homeX(false) + aHit * depth, follow);
      this.bx = lerp(this.bx, this.homeX(true) - bRecv * depth * 0.75, follow);
    } else {
      const bHit = smoothPulse(t, 0.5, 0.7);
      const aRecv = smoothPulse(t, 0.75, 1);
      const bTargetZ = lerp(this.idleZ(1), this.rallyFromZ, Math.max(bHit, 0.35));
      const aTargetZ = lerp(this.idleZ(0), this.rallyToZ, Math.max(aRecv, 0.45));
      this.bz = lerp(this.bz, bTargetZ, follow);
      this.az = lerp(this.az, aTargetZ, follow);
      this.bx = lerp(this.bx, this.homeX(true) - bHit * depth, follow);
      this.ax = lerp(this.ax, this.homeX(false) + aRecv * depth * 0.75, follow);
    }

    // Light ready-stance fidget.
    this.az += Math.sin(timeMs * 0.0028 + this.seed) * 0.012;
    this.bz += Math.sin(timeMs * 0.0031 + this.seed * 2.1) * 0.012;

    this.ax = clamp(this.ax, this.netX - this.half - 0.2, this.netX - 1.1);
    this.bx = clamp(this.bx, this.netX + 1.1, this.netX + this.half + 0.2);
    this.az = clamp(this.az, this.netZ - lateral, this.netZ + lateral);
    this.bz = clamp(this.bz, this.netZ - lateral, this.netZ + lateral);
    this.ay = this.groundY(this.ax, this.az);
    this.by = this.groundY(this.bx, this.bz);
  }

  private homeX(east: boolean): number {
    return east ? this.netX + this.half : this.netX - this.half;
  }

  private idleZ(side: 0 | 1): number {
    return this.baselineAim(Math.floor(this.seed * 8 + side * 13 + this.lastHalf * 3));
  }

  private baselineAim(index: number): number {
    const n = Math.sin(index * 12.9898 + this.seed * 78.233) * 43758.5453;
    const u = n - Math.floor(n);
    return this.netZ + (u - 0.5) * 2.8;
  }
}

function makeShadow(width: number): THREE.Mesh {
  return makeContactShadow({
    radius: Math.min(0.58, width * 0.4),
    squash: 1,
    opacity: 0.88,
  });
}

function setHumanMap(
  mesh: THREE.Mesh,
  sheet: ReturnType<typeof humanSprite>,
): void {
  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial && mat.map !== sheet.texture) {
    mat.map = sheet.texture;
    mat.needsUpdate = true;
  }
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function smoothPulse(t: number, start: number, end: number): number {
  if (t < start || t > end) {
    return 0;
  }
  const u = (t - start) / Math.max(1e-4, end - start);
  return Math.sin(u * Math.PI);
}
